import React, {
  ComponentProps,
  forwardRef,
  HTMLAttributes,
  useContext,
} from 'react';
import { OverlayTriggerProps } from 'react-stately/useOverlayTriggerState';
import {
  DialogContext,
  DialogTrigger,
  OverlayTriggerStateContext,
} from 'react-aria-components';
import { useId } from 'react-aria/useId';
import { FocusableProvider } from 'react-aria/private/interactions/useFocusable';
import { Popover } from '../../Popover';
import { GlobalDomAttributes } from '../../utils/types';
import { FocusableRefValue } from '@react-types/shared';
import { useFocusableRef } from '@react-spectrum/utils';

export interface PopoverTriggerProps
  extends Pick<
      ComponentProps<typeof Popover>,
      | 'placement'
      | 'offset'
      | 'crossOffset'
      | 'shouldFlip'
      | 'shouldCloseOnInteractOutside'
      | 'isKeyboardDismissDisabled'
      | 'scrollRef'
      | 'boundaryElement'
    >,
    OverlayTriggerProps,
    GlobalDomAttributes {
  children: React.ReactNode;
  /**
   * The content of the popover. If you need to render buttons
   * that should close the popover, pass a render function which accepts `close`
   * function in its first argument.
   */
  popoverContent:
    | React.ReactNode
    | ((props: {
        close: () => void;
        titleProps: HTMLAttributes<HTMLElement>;
      }) => React.ReactNode);
  /**
   * If true, popover won't have the default padding.
   * @default false
   */
  noPadding?: boolean;
  /**
   * If true, popover won't have the max width.
   * @default false
   */
  noMaxWidth?: boolean;
}

/**
 *
 * `PopoverTrigger` is a generic component which lets you render arbitrary
 * content in a popover overlay which is linked to a trigger, and is accessible
 * as [dialog](https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/dialog_role).
 */
export const PopoverTrigger = forwardRef<
  FocusableRefValue,
  PopoverTriggerProps
>(function PopoverTrigger(
  {
    children,
    popoverContent,
    isOpen,
    defaultOpen,
    onOpenChange,
    noPadding = false,
    noMaxWidth = false,
    placement,
    offset,
    crossOffset,
    shouldFlip,
    shouldCloseOnInteractOutside,
    scrollRef,
    isKeyboardDismissDisabled,
    ...domProps
  }: PopoverTriggerProps,
  forwardRef
): JSX.Element {
  const triggerRef = useFocusableRef<HTMLElement>(forwardRef);

  return (
    <DialogTrigger
      isOpen={isOpen}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
    >
      <FocusableProvider ref={triggerRef}>{children}</FocusableProvider>
      <PopoverInner
        popoverContent={popoverContent}
        placement={placement}
        offset={offset}
        crossOffset={crossOffset}
        shouldFlip={shouldFlip}
        padded={!noPadding}
        defaultMaxWidth={!noMaxWidth}
        shouldCloseOnInteractOutside={shouldCloseOnInteractOutside}
        isKeyboardDismissDisabled={isKeyboardDismissDisabled}
        domProps={domProps}
      />
    </DialogTrigger>
  );
});

/**
 * Inner Popover component to read Dialog context for accessibility.
 */
const PopoverInner = ({ popoverContent, ...popoverProps }: any) => {
  const dialogProps = useContext(DialogContext);
  const state = React.useContext(OverlayTriggerStateContext);
  const titleId = useId();
  const isFunction = typeof popoverContent === 'function';

  const ariaLabelledBy = isFunction
    ? titleId
    : dialogProps &&
      typeof dialogProps === 'object' &&
      'aria-labelledby' in dialogProps
    ? (dialogProps as { 'aria-labelledby'?: string })['aria-labelledby']
    : undefined;

  return (
    <Popover
      {...popoverProps}
      {...dialogProps}
      aria-labelledby={ariaLabelledBy}
    >
      {isFunction
        ? popoverContent({
            close: () => state?.close(),
            titleProps: { id: titleId },
          })
        : popoverContent}
    </Popover>
  );
};
